import { Theme } from '@mui/material/styles'

import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({

    // ----------------------------------------------
    // Seite

    container: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(6),
    },

    titleBlock: {
      display: 'grid',
      gap: theme.spacing(1.4),
      justifyItems: 'start',
      marginBottom: theme.spacing(1),
    },

    brandLogo: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      maxHeight: 42,
      height: 42,
      maxWidth: 180,
      objectFit: 'contain',
      padding: theme.spacing(.55, .8),
    },
    brandLogoWrap: {
      display: 'inline-block',
      height: 42,
      lineHeight: 0,
      position: 'relative',
    },

    seoIntroTitle: {
      fontWeight: 800,
      lineHeight: 1.18,
      maxWidth: 980,
      [theme.breakpoints.up('md')]: {
        fontSize: 'clamp(1.35rem, 2.4vw, 2rem)',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.45rem',
      },
    },

    listingShell: {
      display: 'grid',
      gap: theme.spacing(4),
      alignItems: 'start',
    },

    listingShellWithSidebar: {
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: '280px minmax(0, 1fr)',
      },
    },

    filterSidebar: {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },

    filterSidebarInner: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      minHeight: 260,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      scrollbarWidth: 'thin',
    },

    filterSidebarTitle: {
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      fontSize: '.86rem',
      fontWeight: 800,
      letterSpacing: '.04em',
      margin: 0,
      minHeight: 52,
      padding: theme.spacing(0, 2),
      textTransform: 'uppercase',
    },

    results: {
      minWidth: 0,
    },

    magazineFeature: {
      borderBottom: '1px solid rgba(49, 39, 28, .12)',
      borderTop: '1px solid rgba(49, 39, 28, .12)',
      margin: theme.spacing(3, 0, 4),
      padding: theme.spacing(2.2, 0),
    },

    magazineFeatureHeader: {
      alignItems: 'baseline',
      display: 'flex',
      gap: theme.spacing(2),
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.4),
      '& span': {
        color: '#83745e',
        fontSize: '.78rem',
        fontWeight: 800,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
      },
      '& h2': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: 'clamp(1.25rem, 2vw, 1.8rem)',
        lineHeight: 1,
        margin: 0,
      },
      [theme.breakpoints.down('sm')]: {
        alignItems: 'start',
        flexDirection: 'column',
      },
    },

    magazineFeatureGrid: {
      display: 'grid',
      gap: theme.spacing(1.5),
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    },

    magazineFeatureCard: {
      alignItems: 'center',
      color: 'inherit',
      display: 'grid',
      gap: theme.spacing(1.4),
      gridTemplateColumns: '92px 1fr auto',
      minHeight: 116,
      padding: theme.spacing(.8, 0),
      textDecoration: 'none',
      transition: 'transform .18s ease',
      '&::after': {
        alignItems: 'center',
        background: 'var(--page-builder-button-bg, #211c17)',
        borderRadius: 999,
        color: 'var(--page-builder-button-text, #fff)',
        content: '"Lesen"',
        display: 'inline-flex',
        fontSize: '.78rem',
        fontWeight: 800,
        height: 34,
        justifyContent: 'center',
        letterSpacing: '.04em',
        padding: theme.spacing(0, 1.5),
        textTransform: 'uppercase',
      },
      '&:hover': {
        transform: 'translateX(4px)',
      },
      '&:hover $magazineFeatureCover': {
        transform: 'translateY(-2px) rotate(-1deg)',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '86px 1fr',
        '&::after': {
          gridColumn: '2',
          justifySelf: 'start',
          marginTop: theme.spacing(.4),
        },
      },
    },

    magazineFeatureCover: {
      aspectRatio: '3 / 4',
      background: '#e6dfd2',
      boxShadow: '0 12px 28px rgba(49, 39, 28, .16)',
      display: 'grid',
      overflow: 'hidden',
      placeItems: 'center',
      position: 'relative',
      transition: 'transform .18s ease',
      '& img': {
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
      '& span': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '.95rem',
        padding: theme.spacing(1),
        textAlign: 'center',
      },
    },

    magazineFeatureText: {
      alignContent: 'center',
      display: 'grid',
      gap: theme.spacing(.35),
      minWidth: 0,
      '& small': {
        color: '#83745e',
        fontWeight: 800,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
      },
      '& strong': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.12rem',
        lineHeight: 1.05,
      },
      '& span': {
        color: 'rgba(33, 31, 28, .68)',
        fontSize: '.9rem',
      },
    },

    magazineFaqSection: {
      background: '#fffdf8',
      border: `1px solid ${theme.palette.divider}`,
      margin: theme.spacing(3, 0, 4),
      padding: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
      },
    },

    magazineFaqHeader: {
      display: 'grid',
      gap: theme.spacing(.5),
      marginBottom: theme.spacing(2),
      '& span': {
        color: '#83745e',
        fontSize: '.78rem',
        fontWeight: 800,
        textTransform: 'uppercase',
      },
      '& h2': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.9rem',
        lineHeight: 1.05,
        margin: 0,
      },
    },

    magazineFaqGrid: {
      display: 'grid',
      gap: theme.spacing(2),
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    },

    magazineFaqItem: {
      borderTop: `1px solid ${theme.palette.divider}`,
      display: 'grid',
      gap: theme.spacing(.75),
      paddingTop: theme.spacing(1.5),
      '& small': {
        color: '#83745e',
        fontSize: '.76rem',
        fontWeight: 800,
        letterSpacing: '.08em',
        textTransform: 'uppercase',
      },
      '& h3': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.22rem',
        lineHeight: 1.16,
        margin: 0,
      },
      '& div': {
        display: 'grid',
        gap: theme.spacing(.75),
      },
      '& p': {
        color: 'rgba(33, 31, 28, .72)',
        lineHeight: 1.6,
        margin: 0,
      },
      '& a': {
        color: 'var(--page-builder-button-bg, #211c17)',
        fontWeight: 800,
        justifySelf: 'start',
        textDecoration: 'none',
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
    },

    resultsHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
      },
    },

    count: {
      fontWeight: 600,
      letterSpacing: '.04em',
      textTransform: 'uppercase',
    },

    mobileFilterButton: {
      [theme.breakpoints.up('md')]: {
        display: 'none !important',
      },
    },

    startHr: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    
    // ----------------------------------------------

  })
)

export default useStyles
